import {
    CreditCardFilled,
    DownloadOutlined,
    ExclamationCircleFilled,
} from "@ant-design/icons";
import {
    Button,
    ConfigProvider,
    DatePicker,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { rightsService } from "../../services/rightsService";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { frenchDate, frenchDateSimple } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";

const { confirm } = Modal;
const { Option } = Select;
const { RangePicker } = DatePicker;
const dateFormat = "DD/MM/YYYY";

/* ── Carte statistique ─────────────────────────────────────────── */
const StatCard = ({ label, sublabel, value, isAmount, icon, bg }) => (
    <div className="col-sm-6 col-xl-3">
        <div
            className="card border-0 h-100"
            style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}
        >
            <div style={{ height: 4, background: bg, borderRadius: "14px 14px 0 0" }} />
            <div className="card-body" style={{ padding: "1.2rem 1.4rem" }}>
                <div className="d-flex align-items-start justify-content-between">
                    <div>
                        <p
                            className="text-muted mb-1"
                            style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em" }}
                        >
                            {label}
                        </p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#1a1a2e", lineHeight: 1.2 }}>
                            {isAmount
                                ? <>{displayNumbers(Math.trunc(value ?? 0))} <small style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>FCFA</small></>
                                : (value ?? 0)
                            }
                        </h4>
                    </div>
                    <div
                        style={{
                            width: 46, height: 46, borderRadius: "50%",
                            background: bg + "22",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                    >
                        <i className={icon} style={{ fontSize: 22, background: bg, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} />
                    </div>
                </div>
                <small className="text-muted" style={{ fontSize: 11 }}>{sublabel}</small>
            </div>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════════ */

const EnAttenteDEncaissement = () => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loadingContrats, setLoadingContrats] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});

    const [selecting, setSelecting] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [modalModePaiement, setModalModePaiement] = useState(false);
    const [selectedContrat, setSelectedContrat] = useState(null);
    const [contractsToPay, setContractsToPay] = useState([]);

    const [modePaiement, setModePaiement] = useState("ESPECES");
    const [numeroPaiement, setNumeroPaiement] = useState("");
    const [reference, setReference] = useState("");
    const [banque, setBanque] = useState("");
    const [dateVirement, setDateVirement] = useState(null);
    const [modePaiementForm] = Form.useForm();

    const [search, setSearch] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedCompagnies, setSelectedCompagnies] = useState([]);
    const [membres, setMembres] = useState([]);
    const [banques, setBanques] = useState([]);
    const [count, setCount] = useState({});

    const navigate = useNavigate();

    /* ── Chargement données ──────────────────────────────────────── */
    const fetchData = (params = {}) => {
        setLoadingContrats(true);
        const filters = params.filters || {};
        axiosInstance
            .get("/api/contrat/en-attente-encaissement", {
                params: {
                    page: (params.page ?? currentPage) - 1,
                    size: params.size ?? pageSize,
                    search: filters.search ?? search,
                    startDate: filters.startDate ?? null,
                    endDate: filters.endDate ?? null,
                    compagnie: (filters.compagnie ?? selectedCompagnies).join?.(",") || "",
                },
            })
            .then((res) => {
                const { content, totalElements } = res.data;
                const rows = content.map((c, i) => ({
                    key: c.codeId,
                    codeId: c.codeId,
                    id: i,
                    numeroPolice: c.numeroPoliceM || c.numeroPoliceP,
                    souscripteur: c.prenom ? `${c.prenom} ${c.nom}` : c.nom,
                    organisme: c.raisonSociale,
                    immatriculation: c.immatriculation || "—",
                    categorie: c.catNom || c.codeCat || "—",
                    dateEffet: c.dateEffet ? frenchDate(c.dateEffet) : "—",
                    dateEcheance: c.dateEcheance ? frenchDateSimple(c.dateEcheance) : "—",
                    primeNette: c.primeNette ?? 0,
                    primeTotale: c.primeTotale ?? 0,
                    accessoires: c.accessoires ?? 0,
                    taxes: c.taxes ?? 0,
                    regle: c.reglements ? "REGLE" : "NON REGLE",
                    data: c,
                }));
                setData(rows);
                setTotal(totalElements);
            })
            .catch((err) => console.error("Erreur chargement contrats:", err))
            .finally(() => setLoadingContrats(false));
    };

    useEffect(() => {
        fetchData({ page: currentPage, size: pageSize });
    }, [currentPage, pageSize]);

    useEffect(() => {
        axiosInstance.get("/api/contrat/count")
            .then((res) => setCount(res.data))
            .catch(console.log);

        axiosInstance.get("/api/parametre/type/BANQUE")
            .then((res) => setBanques(res.data))
            .catch(console.log);

        axiosInstance.get("/api/organisme/assurance").then((res) => {
            const membs = res.data.map((m, i) => ({ key: i, text: m.raisonSociale, value: m.code }));
            const dtrf = [
                { key: "DTRF", text: "DTRF", value: "DTRF" },
                { key: "LOME", text: "DTRF-LOME", value: "LOME" },
                { key: "KPALIME", text: "DTRF-KPALIME", value: "KPALIME" },
                { key: "ATAKPAME", text: "DTRF-ATAKPAME", value: "ATAKPAME" },
                { key: "SOKODE", text: "DTRF-SOKODE", value: "SOKODE" },
                { key: "KARA", text: "DTRF-KARA", value: "KARA" },
                { key: "DAPAONG", text: "DTRF-DAPAONG", value: "DAPAONG" },
                { key: "STCA", text: "STCA", value: "STCA" },
            ];
            setMembres([...membs, ...dtrf]);
        }).catch(console.log);
    }, []);

    /* ── Totaux de la page courante ──────────────────────────────── */
    const totalPrimeTotale = data.reduce((s, r) => s + (r.primeTotale || 0), 0);
    const totalPrimeNette = data.reduce((s, r) => s + (r.primeNette || 0), 0);

    /* ── Colonnes ────────────────────────────────────────────────── */
    const columns = [
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
            render: (v) => (
                <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#1677ff", fontSize: 13 }}>
                    {v || "—"}
                </span>
            ),
        },
        {
            title: "Souscripteur",
            dataIndex: "souscripteur",
            key: "souscripteur",
            render: (v) => <span style={{ fontWeight: 600, color: "#1a1a2e" }}>{v}</span>,
        },
        {
            title: "Immatriculation",
            dataIndex: "immatriculation",
            key: "immatriculation",
            render: (v) => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{v}</span>,
        },
        {
            title: "Catégorie",
            dataIndex: "categorie",
            key: "categorie",
            render: (v) => (
                <Tag style={{ background: "#f0f5ff", color: "#1677ff", border: "1px solid #91caff", borderRadius: 6, fontWeight: 700, fontSize: 12 }}>
                    {v}
                </Tag>
            ),
        },
        {
            title: "Prime nette",
            dataIndex: "primeNette",
            key: "primeNette",
            align: "right",
            render: (v) => (
                <span style={{ fontWeight: 600 }}>
                    {displayNumbers(Math.trunc(v))} <small style={{ color: "#888" }}>FCFA</small>
                </span>
            ),
        },
        {
            title: "Prime TTC",
            dataIndex: "primeTotale",
            key: "primeTotale",
            align: "right",
            render: (v) => (
                <span style={{ fontWeight: 700, color: "#1a1a2e" }}>
                    {displayNumbers(Math.trunc(v))} <small style={{ fontWeight: 400, color: "#888" }}>FCFA</small>
                </span>
            ),
        },
        {
            title: "Date effet",
            dataIndex: "dateEffet",
            key: "dateEffet",
            render: (v) => <span style={{ color: "#555", fontSize: 13 }}>{v}</span>,
        },
        {
            title: "Date échéance",
            dataIndex: "dateEcheance",
            key: "dateEcheance",
            render: (v) => <span style={{ color: "#555", fontSize: 13 }}>{v}</span>,
        },
        {
            title: "Statut",
            dataIndex: "regle",
            key: "regle",
            render: (v) =>
                v === "REGLE" ? (
                    <Tag color="success" style={{ fontWeight: 700, borderRadius: 20, padding: "2px 12px" }}>Réglé</Tag>
                ) : (
                    <Tag color="warning" style={{ fontWeight: 700, borderRadius: 20, padding: "2px 12px" }}>Non réglé</Tag>
                ),
            filters: [
                { text: "Réglé", value: "REGLE" },
                { text: "Non réglé", value: "NON REGLE" },
            ],
            filteredValue: filteredInfo.regle || null,
            onFilter: (value, record) => record.regle === value,
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, record) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                    {rightsService("HLA037") && record.numeroPolice && record.regle !== "REGLE" && (
                        <Tooltip title="Encaisser">
                            <button
                                type="button"
                                className="btn btn-sm btn-success btn-icon rounded-pill"
                                onClick={() => { setSelectedContrat(record); setModalModePaiement(true); }}
                            >
                                <i className="bx bxs-credit-card" />
                            </button>
                        </Tooltip>
                    )}
                    <Tooltip title="Voir le contrat">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary btn-icon rounded-pill"
                            onClick={() => navigate(`/details-contrat/${record.codeId}`)}
                        >
                            <i className="bx bx-dots-vertical-rounded" />
                        </button>
                    </Tooltip>
                </div>
            ),
        },
    ];

    /* ── Sélection multiple ──────────────────────────────────────── */
    const onSelectChange = (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows);
    };

    const rowSelection = { selectedRowKeys, onChange: onSelectChange };

    const Footer_selection = () => {
        const nonRegle = selectedRows.filter((r) => r.regle === "NON REGLE");
        return nonRegle.length > 0 ? (
            <div className="row">
                <div className="col-auto">
                    <Button
                        icon={<CreditCardFilled />}
                        style={{ color: "white", background: "#059669", border: "none" }}
                        onClick={() => { setContractsToPay(nonRegle); setModalModePaiement(true); }}
                    >
                        Encaisser {nonRegle.length} contrat{nonRegle.length > 1 ? "s" : ""}
                    </Button>
                </div>
            </div>
        ) : null;
    };

    /* ── Filtres ─────────────────────────────────────────────────── */
    const handleTableChange = (pagination, filters, sorter) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const handleDateChange = (_, dateStrings) => {
        const [s, e] = dateStrings;
        if (!s || !e) return;
        const [sd, sm, sy] = s.split("/");
        const [ed, em, ey] = e.split("/");
        const startDate = `${sy}-${sm}-${sd}`;
        const endDate = `${ey}-${em}-${ed}`;
        setSelectedDate([startDate, endDate]);
        fetchData({ page: 1, filters: { startDate, endDate, compagnie: selectedCompagnies } });
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        if (val.length === 0 || val.length >= 3) {
            fetchData({ page: 1, filters: { search: val, startDate: selectedDate?.[0], endDate: selectedDate?.[1], compagnie: selectedCompagnies } });
            setCurrentPage(1);
        }
    };

    const filteringOnCompanies = (companies) => {
        setSelectedCompagnies(companies);
        fetchData({ page: 1, filters: { search, startDate: selectedDate?.[0], endDate: selectedDate?.[1], compagnie: companies } });
        setCurrentPage(1);
    };

    /* ── Encaissement ────────────────────────────────────────────── */
    const reglementContrat = () => {
        const contrat = selectedContrat;
        if (modePaiement === "ESPECES") {
            axiosInstance
                .get(`/api/reglement/especes/contrat/codeId/${contrat.codeId}/${LOGGED_USER.codeId}`)
                .then(() => { toast("Règlement effectué avec succès !"); fetchData({ page: currentPage }); })
                .catch(() => toast("Erreur lors du règlement"));
        } else {
            axiosInstance
                .get(`/api/reglement/contrat/codeId/${contrat.codeId}/${LOGGED_USER.codeId}/${modePaiement}/${numeroPaiement || reference}/${banque || reference}`)
                .then(() => { toast("Règlement effectué avec succès !"); fetchData({ page: currentPage }); })
                .catch(() => toast("Erreur lors du règlement"));
        }
        setModalModePaiement(false);
    };

    const reglementsContrats = () => {
        const allContrats = contractsToPay.map((c) => c.data);
        if (modePaiement === "ESPECES") {
            axiosInstance
                .post("/api/reglement/especes/contrats", JSON.stringify(allContrats))
                .then(() => { toast("Règlements effectués avec succès !"); fetchData({ page: currentPage }); })
                .catch(() => toast("Erreur lors des règlements"));
        } else {
            axiosInstance
                .post(`/api/reglement/else/${modePaiement}/${numeroPaiement}/${banque}`, JSON.stringify(allContrats))
                .then(() => { toast("Règlements effectués avec succès !"); fetchData({ page: currentPage }); })
                .catch(() => toast("Erreur lors des règlements"));
        }
        setModalModePaiement(false);
    };

    const alertReglement = () => {
        confirm({
            title: "Confirmer l'encaissement",
            icon: <ExclamationCircleFilled />,
            content: "Toutes vos actions sont historisées.",
            okText: "Oui, encaisser",
            okType: "primary",
            cancelText: "Annuler",
            onOk: contractsToPay.length > 0 ? reglementsContrats : reglementContrat,
        });
    };

    /* ── Export Excel ────────────────────────────────────────────── */
    const exportToExcel = async () => {
        setExporting(true);
        try {
            // Charger toutes les données pour l'export
            const res = await axiosInstance.get("/api/contrat/en-attente-encaissement", {
                params: {
                    page: 0,
                    size: 10000,
                    search,
                    startDate: selectedDate?.[0] ?? null,
                    endDate: selectedDate?.[1] ?? null,
                    compagnie: selectedCompagnies.join?.(",") || "",
                },
            });

            const rows = res.data.content || [];

            const workbook = new ExcelJS.Workbook();
            workbook.creator = "PoolIns";
            const ws = workbook.addWorksheet("En attente d'encaissement");

            // En-têtes
            const headers = [
                "N° Police", "Souscripteur", "Immatriculation", "Catégorie",
                "Prime Nette (FCFA)", "Prime TTC (FCFA)", "Accessoires (FCFA)", "Taxes (FCFA)",
                "Date Effet", "Date Échéance", "Statut Règlement",
            ];
            const headerRow = ws.addRow(headers);
            headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
            headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1677FF" } };
            headerRow.alignment = { horizontal: "center", vertical: "middle" };
            headerRow.height = 20;

            let sumPrimeNette = 0;
            let sumPrimeTotale = 0;

            rows.forEach((c) => {
                const primeNette = Math.trunc(c.primeNette ?? 0);
                const primeTotale = Math.trunc(c.primeTotale ?? 0);
                sumPrimeNette += primeNette;
                sumPrimeTotale += primeTotale;

                ws.addRow([
                    c.numeroPoliceM || c.numeroPoliceP || "",
                    c.prenom ? `${c.prenom} ${c.nom}` : c.nom || "",
                    c.immatriculation || "",
                    c.catNom || c.codeCat || "",
                    primeNette,
                    primeTotale,
                    Math.trunc(c.accessoires ?? 0),
                    Math.trunc(c.taxes ?? 0),
                    c.dateEffet ? dayjs(c.dateEffet).format("DD/MM/YYYY") : "",
                    c.dateEcheance ? dayjs(c.dateEcheance).format("DD/MM/YYYY") : "",
                    c.reglements ? "RÉGLÉ" : "NON RÉGLÉ",
                ]);
            });

            // Ligne totaux
            const totalRow = ws.addRow([
                "TOTAL", "", "", "",
                sumPrimeNette, sumPrimeTotale, "", "", "", "", "",
            ]);
            totalRow.font = { bold: true };
            totalRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F5FF" } };

            // Largeurs colonnes
            const widths = [20, 28, 18, 14, 18, 18, 16, 14, 14, 14, 14];
            ws.columns.forEach((col, i) => { col.width = widths[i] || 16; });

            // Format numérique pour les montants
            [5, 6, 7, 8].forEach((colIdx) => {
                ws.getColumn(colIdx).numFmt = '#,##0';
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const now = dayjs().format("YYYYMMDD_HHmmss");
            saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
                `en_attente_encaissement_${now}.xlsx`);
            toast("Export Excel généré avec succès !");
        } catch (err) {
            console.error("Erreur export Excel:", err);
            toast("Erreur lors de l'export Excel");
        } finally {
            setExporting(false);
        }
    };

    /* ── Rendu ───────────────────────────────────────────────────── */
    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"CAISSE"} />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* ── En-tête ─────────────────────────────────── */}
                            <div className="mb-5">
                                <h4 className="mb-1 fw-bold" style={{ color: "#1a1a2e", fontSize: 22 }}>
                                    <i className="bx bx-time-five me-2" style={{ color: "#f59e0b", verticalAlign: "middle" }} />
                                    Contrats en attente d'encaissement
                                </h4>
                                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                    Contrats validés dont le règlement n'a pas encore été encaissé
                                </p>
                            </div>

                            {/* ── Cartes statistiques ─────────────────────── */}
                            <div className="row g-4 mb-5">
                                <StatCard
                                    label="En attente"
                                    sublabel="Contrats non encaissés"
                                    value={total}
                                    icon="bx bx-time-five bx-sm"
                                    bg="linear-gradient(135deg,#f59e0b,#fbbf24)"
                                />
                                <StatCard
                                    label="Prime TTC (page)"
                                    sublabel="Total prime TTC — page courante"
                                    value={totalPrimeTotale}
                                    isAmount
                                    icon="bx bx-money bx-sm"
                                    bg="linear-gradient(135deg,#1677ff,#4299e1)"
                                />
                                <StatCard
                                    label="Prime nette (page)"
                                    sublabel="Total prime nette — page courante"
                                    value={totalPrimeNette}
                                    isAmount
                                    icon="bx bx-line-chart bx-sm"
                                    bg="linear-gradient(135deg,#7c3aed,#a78bfa)"
                                />
                                <StatCard
                                    label="Contrats actifs"
                                    sublabel="Total contrats validés"
                                    value={count?.valideCount ?? 0}
                                    icon="bx bx-check-circle bx-sm"
                                    bg="linear-gradient(135deg,#059669,#34d399)"
                                />
                            </div>

                            {/* ── Tableau ──────────────────────────────────── */}
                            <div
                                className="card border-0"
                                style={{ borderRadius: 14, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", overflow: "hidden" }}
                            >
                                {/* Barre filtres */}
                                <div
                                    className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-3"
                                    style={{ background: "#fafbff", borderBottom: "1px solid #e8edf5", padding: "1rem 1.4rem" }}
                                >
                                    <div>
                                        <h6 className="mb-0 fw-bold" style={{ color: "#1a1a2e" }}>
                                            Liste des contrats en attente
                                        </h6>
                                        <small className="text-muted">
                                            {total} contrat{total > 1 ? "s" : ""}
                                        </small>
                                    </div>

                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                        <Input.Search
                                            placeholder="Rechercher (police, assuré…)"
                                            allowClear
                                            onChange={handleSearchChange}
                                            style={{ width: 220, borderRadius: 8 }}
                                            size="middle"
                                        />
                                        <ConfigProvider locale={frFR}>
                                            <RangePicker
                                                format={dateFormat}
                                                onChange={handleDateChange}
                                                placeholder={["Date début", "Date fin"]}
                                                style={{ borderRadius: 8 }}
                                            />
                                        </ConfigProvider>
                                        {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" &&
                                            (LOGGED_USER?.agence?.typeAgence === "SIEGE" || LOGGED_USER?.agence === null) && (
                                                <Select
                                                    mode="multiple"
                                                    placeholder="Filtrer par compagnie"
                                                    onChange={filteringOnCompanies}
                                                    options={membres.map((m) => ({ label: m.text, value: m.value, key: m.value }))}
                                                    style={{ minWidth: 200 }}
                                                    maxTagCount="responsive"
                                                />
                                            )}
                                        <Space>
                                            <span style={{ fontSize: 13, color: "#555" }}>Sélection</span>
                                            <Switch checked={selecting} onChange={setSelecting} size="small" />
                                        </Space>
                                        <Tooltip title="Exporter en Excel">
                                            <Button
                                                icon={<DownloadOutlined />}
                                                loading={exporting}
                                                onClick={exportToExcel}
                                                style={{ borderRadius: 8, background: "#059669", border: "none", color: "#fff" }}
                                            >
                                                Excel
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>

                                {/* Table */}
                                <ConfigProvider locale={frFR}>
                                    <Spin spinning={loadingContrats}>
                                        <Table
                                            rowSelection={selecting ? rowSelection : null}
                                            columns={columns}
                                            dataSource={data}
                                            rowKey="codeId"
                                            size="middle"
                                            onChange={handleTableChange}
                                            pagination={{
                                                current: currentPage,
                                                pageSize: pageSize,
                                                total: total,
                                                showSizeChanger: true,
                                                pageSizeOptions: ["10", "20", "50", "100"],
                                                showTotal: (t) => `${t} contrat${t > 1 ? "s" : ""}`,
                                            }}
                                            rowClassName={(_, index) =>
                                                index % 2 === 0 ? "table-row-even" : "table-row-odd"
                                            }
                                            footer={() => selecting ? <Footer_selection /> : null}
                                            style={{ borderRadius: 0 }}
                                        />
                                    </Spin>
                                </ConfigProvider>
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade" />
                    </div>
                </div>
            </div>

            {/* ── Modal mode de paiement ──────────────────────────── */}
            <Modal
                title={
                    <span style={{ fontWeight: 700 }}>
                        <i className="bx bxs-credit-card me-2" style={{ color: "#1677ff" }} />
                        Encaisser la prime
                    </span>
                }
                centered
                open={modalModePaiement}
                okText="Confirmer"
                cancelText="Annuler"
                onOk={alertReglement}
                onCancel={() => { setModalModePaiement(false); setContractsToPay([]); }}
                afterClose={() => { modePaiementForm.resetFields(); setModePaiement("ESPECES"); }}
            >
                <div className="container" style={{ paddingTop: 8 }}>
                    <Form form={modePaiementForm} layout="vertical">
                        <Form.Item
                            name="modePaiement"
                            label="Mode de paiement"
                            rules={[{ required: true, message: "Champ obligatoire" }]}
                        >
                            <Select
                                onChange={(v) => setModePaiement(v)}
                                placeholder="Sélectionnez un mode de paiement"
                                style={{ borderRadius: 8 }}
                            >
                                <Option value="ESPECES">Espèces</Option>
                                <Option value="BANQUE">Chèque bancaire</Option>
                                <Option value="VIREMENT">Virement bancaire</Option>
                                <Option value="MOBILE_MONEY">Mobile Money (Flooz / TMoney)</Option>
                            </Select>
                        </Form.Item>

                        {(modePaiement === "BANQUE" || modePaiement === "MOBILE_MONEY") && (
                            <Form.Item
                                name="numeroPaiement"
                                label={modePaiement === "BANQUE" ? "Numéro de chèque" : "Numéro Flooz / TMoney"}
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Input
                                    placeholder={modePaiement === "BANQUE" ? "Ex : 001234" : "Numéro de téléphone"}
                                    onChange={(e) => setNumeroPaiement(e.target.value)}
                                />
                            </Form.Item>
                        )}

                        {(modePaiement === "BANQUE" || modePaiement === "VIREMENT") && (
                            <Form.Item
                                name="banque"
                                label="Banque"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Select onChange={(v) => setBanque(v)} placeholder="Sélectionnez la banque">
                                    {banques.map((b) => (
                                        <Option key={b.parametreId} value={b.libelleParametre}>
                                            {b.libelleParametre}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}

                        {(modePaiement === "MOBILE_MONEY" || modePaiement === "VIREMENT") && (
                            <Form.Item
                                name="referencePaiement"
                                label="Référence du paiement"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <Input
                                    placeholder="Numéro de référence"
                                    onChange={(e) => setReference(e.target.value)}
                                />
                            </Form.Item>
                        )}

                        {modePaiement === "VIREMENT" && (
                            <Form.Item
                                name="dateVirement"
                                label="Date du virement"
                                rules={[{ required: true, message: "Champ obligatoire" }]}
                            >
                                <DatePicker
                                    placeholder="Date du virement"
                                    onChange={(_, v) => setDateVirement(v)}
                                    style={{ width: "100%" }}
                                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                                />
                            </Form.Item>
                        )}
                    </Form>
                </div>
            </Modal>

            <div className="layout-overlay layout-menu-toggle" />
            <div className="drag-target" />
        </div>
    );
};

export default EnAttenteDEncaissement;
